'use client';

import React from 'react';
import Markdoc from '@markdoc/markdoc';
import yaml from 'js-yaml';
import { Tag } from '@markdoc/markdoc';
import { slugifyWithCounter } from '@sindresorhus/slugify';
import { Fence } from '../features/docs/Fence';
import '@/styles/tailwind.css';

const documentSlugifyMap = new Map();

// **Custom Nodes for Markdoc**
const nodesForMarkdoc = {
  document: {
    ...Markdoc.nodes.document,
    transform(node: any, config: any) {
      documentSlugifyMap.set(config, slugifyWithCounter());
      return new Tag(
        'div',
        { frontmatter: yaml.load(node.attributes?.frontmatter ?? '') },
        node.transformChildren(config)
      );
    },
  },
  heading: {
    ...Markdoc.nodes.heading,
    transform(node: any, config: any) {
      const slugify = documentSlugifyMap.get(config);
      const attributes = node.transformAttributes(config);
      const children = node.transformChildren(config);
      const text = children.filter((child: any) => typeof child === 'string').join(' ');
      const id = attributes.id ?? slugify(text);

      return new Tag(
        `h${node.attributes.level}`,
        { ...attributes, id },
        children
      );
    },
  },
  fence: {
    render: Fence,
    attributes: {
      language: { type: String },
    },
  },
};

// **Main Markdown Renderer Component**
interface MarkdownRendererProps {
  markdown: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ markdown }) => {
  // Parse and transform Markdown with Markdoc
  const ast = Markdoc.parse(markdown);
  // const frontmatter = yaml.load(ast.attributes?.frontmatter ?? '') || {};

  // Ensure `nodes` is an array
  // @ts-expect-error `nodes` is an object
  let nodes = Markdoc.transform(ast, { ...nodesForMarkdoc });
  if (!Array.isArray(nodes)) {
    // @ts-expect-error `nodes` is an object
    nodes = [nodes]; // Convert to array if it's an object
  }

  // Ensure collectSections gets an array
  // const tableOfContents = collectSections(nodes || []);

  return (
    <div className="w-full">
      <div
        className={[
          'prose prose-slate dark:prose-invert max-w-none overflow-hidden',
          // headings
          'prose-headings:font-extrabold prose-headings:tracking-tight prose-headings:text-foreground',
          'prose-h1:text-2xl prose-h1:leading-[1.1] prose-h1:mb-4 sm:prose-h1:text-3xl',
          'prose-h2:text-xl prose-h2:leading-[1.15] prose-h2:mt-8 prose-h2:mb-3',
          'prose-h3:text-base prose-h3:leading-[1.2] prose-h3:mt-6 prose-h3:mb-2',
          // body
          'prose-p:text-foreground/90 prose-p:leading-relaxed prose-p:text-[0.95rem]',
          'prose-li:text-foreground/90 prose-li:leading-relaxed prose-li:my-1',
          'prose-strong:text-foreground prose-strong:font-bold',
          // links
          'prose-a:text-primary prose-a:font-semibold prose-a:no-underline prose-a:underline-offset-2 hover:prose-a:underline',
          // pre / code (sharp v2.1)
          'prose-pre:rounded-none prose-pre:border prose-pre:border-border',
          'prose-code:rounded-none prose-code:border prose-code:border-border prose-code:bg-muted/40 prose-code:px-1 prose-code:py-0.5 prose-code:before:content-none prose-code:after:content-none',
          // blockquote
          'prose-blockquote:border-l-[3px] prose-blockquote:border-primary prose-blockquote:bg-primary/[0.04] prose-blockquote:px-4 prose-blockquote:py-3 prose-blockquote:not-italic prose-blockquote:font-medium prose-blockquote:text-foreground',
          // hr
          'prose-hr:border-border',
        ].join(' ')}
      >
        {Markdoc.renderers.react(nodes, React)}
      </div>
    </div>
  );
};

export default MarkdownRenderer;
